<template>
  <div class="donut-root">
    <div class="donut-wrapper">
      <svg viewBox="0 0 120 120" class="donut-svg">
        <circle
          v-for="(seg, i) in segments"
          :key="i"
          class="donut-segment"
          cx="60"
          cy="60"
          r="45"
          fill="none"
          :stroke="seg.color"
          stroke-width="18"
          :stroke-dasharray="
            animated ? seg.dashArray : `0 ${seg.circumference}`
          "
          :stroke-dashoffset="seg.dashOffset"
          :transform="'rotate(-90 60 60)'"
          @mousemove="onHover($event, seg.tooltip)"
          @mouseleave="onLeave"
        />
      </svg>
    </div>
    <div class="donut-legend">
      <div v-for="item in items" :key="item.key" class="donut-item">
        <span class="donut-dot" :style="{ background: item.color }"></span>
        {{ item.label }}
        <strong>{{ legendValue(item) }}</strong>
      </div>
    </div>
    <div v-if="showTotal" class="donut-total">
      <strong>{{ total }}</strong> totalt
    </div>
  </div>
</template>
<script>
export default {
  props: {
    // [{ key, label, value, color }]
    items: { type: Array, required: true },
    showTotal: { type: Boolean, default: false }
  },
  emits: ['tooltip'],
  data() {
    return { animated: false };
  },
  computed: {
    total() {
      return this.items.reduce((s, i) => s + (Number(i.value) || 0), 0);
    },
    segments() {
      const total = this.total;
      const circumference = 2 * Math.PI * 45;
      if (!total) return [];
      const segments = [];
      let offset = 0;
      for (const item of this.items) {
        const v = Number(item.value) || 0;
        if (v <= 0) continue;
        const pct = Math.round((v / total) * 100);
        const len = (v / total) * circumference;
        segments.push({
          color: item.color,
          dashArray: `${len} ${circumference - len}`,
          dashOffset: -offset,
          circumference,
          tooltip: `${item.label}: ${v} (${pct}%)`
        });
        offset += len;
      }
      return segments;
    }
  },
  mounted() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.animated = true;
      });
    });
  },
  methods: {
    legendValue(item) {
      const value = Number(item.value) || 0;
      if (!this.total || !value) return '–';
      const pct = Math.round((value / this.total) * 100);
      return `${value} (${pct}%)`;
    },
    onHover(event, text) {
      this.$emit('tooltip', {
        visible: true,
        text,
        x: event.clientX,
        y: event.clientY
      });
    },
    onLeave() {
      this.$emit('tooltip', { visible: false, text: '', x: 0, y: 0 });
    }
  }
};
</script>
<style scoped>
.donut-root {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.donut-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  min-height: 160px;
  margin-bottom: 16px;
}

.donut-svg {
  width: 160px;
  height: 160px;
  flex-shrink: 0;
}

.donut-segment {
  transition:
    stroke-dasharray 0.8s cubic-bezier(0.25, 0.1, 0.25, 1),
    stroke-width 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  cursor: pointer;
}

.donut-segment:hover {
  stroke-width: 22;
}

.donut-legend {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.donut-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1rem;
  color: #5a6370;
}

.donut-item strong {
  margin-left: auto;
  color: #2c3e50;
}

.donut-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.donut-total {
  text-align: center;
  margin-top: 12px;
  font-size: 1.2rem;
  color: #878b9d;
}

.donut-total strong {
  font-size: 1.6rem;
  font-weight: 800;
  color: var(--color-dark-blue);
}
</style>
